/**
 * Canonical `data-testid` registry — SINGLE SOURCE for every UI task.
 *
 * Source of truth: `docs/tdd/drive-music-streaming-pwa/ui-contract.md`
 *   - §3 per-route interactive-element tables (+ shared app-shell table),
 *   - §4 form contracts incl. the "Testid reconciliation (canonical)" callout,
 *   - §6 Data-Testid Registry,
 *   - plus exactly two testids the contract names outside those sections:
 *     `retry-sign-in-button` (§9 /signin/error) and `queue-show-more-button`
 *     (§10.6 scale table / pagination affordance).
 *
 * DERIVED STATIC COUNT: 90 constants. Every value below appears VERBATIM in
 * the contract — nothing renamed, nothing invented.
 *
 * Why the §6 "222 consolidated distinct candidates" figure is NOT reproducible:
 * §6 explicitly enumerates only ~75 rows, then defers "+147 more distinct
 * candidates (see §3 per-route tables)" — but §3 COLLAPSES those remainders
 * into repeated per-entity patterns (`link:album-card →#/albums/*` ×27,
 * `btn:Actions for *` ×20, `btn:Move-reorder *` ×120, `button:Play *` ×42, …).
 * Those are DYNAMIC per-entity testids (one per album/track/genre/playlist),
 * not nameable static constants; they are served by the typed FACTORIES below,
 * whose shapes follow the contract's own examples (`genre-filter-chip-<name>`,
 * `sync-stat-<label>`, `<rank>-<slug>-<plays>-plays-button`, `history-row`).
 * Fabricating ~130 filler constants to reach "222" would poison every
 * downstream UI task, so it was deliberately not done.
 *
 * The 25 §6 "Top Songs" rows (`1-15-stepradiohead-in-rainbows-210-plays-button`
 * …) are ALSO factory-tier, not constants: each name embeds mock seed data
 * (rank + crawl-truncated slug + a live play count) and two of them contain
 * `--` truncation artifacts that are not even valid kebab-case. §3 itself
 * collapses them to the pattern `<rank>-<slug>-<plays>-plays-button` — use
 * {@link topSongRowButton}.
 *
 * Superseded candidates (§4 reconciliation) are intentionally ABSENT and must
 * never be used: `name-example-com-input` (→ `EMAIL_TO_INVITE_INPUT`) and
 * `artists-albums-songs-genres-input` (→ `SEARCH_INPUT`).
 *
 * Note: the contract's crawl derives repeated same-label controls with ordinal
 * suffixes (`remove-from-queue-button`, `-2` … `-6`; `promote-button-2` …).
 * The explicitly enumerated ordinals are kept verbatim below; positions beyond
 * the enumerated ones follow the same convention via {@link ordinalTestId}.
 */
export const TESTIDS = {
    // ── Shared app shell (§3 shell table / §6) ────────────────────────────────
    STEAMYMUSIC_HOME_LINK: 'steamymusic-home-link',
    QUEUE_LINK: 'queue-link',
    SETTINGS_AND_PROFILE_LINK: 'settings-and-profile-link',
    NAV_HOME_LINK: 'nav-home-link',
    NAV_BROWSE_LINK: 'nav-browse-link',
    NAV_SEARCH_LINK: 'nav-search-link',
    NAV_FAVES_LINK: 'nav-faves-link',
    NAV_STATS_LINK: 'nav-stats-link',
    NAV_MORE_BUTTON: 'nav-more-button',
    PAGE_HEADING: 'page-heading',
    // ── /browse/artists · /browse/genres · /browse/albums (§3) ────────────────
    ARTISTS_LINK: 'artists-link',
    GENRES_LINK: 'genres-link',
    /**
     * The ALBUMS tab. Net-new (the §1 crawl predates the tab), derived by the
     * contract's own rule — kebab-case of the accessible name "ALBUMS".
     */
    ALBUMS_LINK: 'albums-link',
    LOAD_MORE_ARTISTS_BUTTON: 'load-more-artists-button',
    // ── /artists/:id (§3) ─────────────────────────────────────────────────────
    ALL_ARTISTS_LINK: 'all-artists-link',
    // ── /albums/:id (§3; play/shuffle shared with /playlists/:id) ─────────────
    PLAY_BUTTON: 'play-button',
    SHUFFLE_BUTTON: 'shuffle-button',
    TOGGLE_FAVORITE_ALBUM_BUTTON: 'toggle-favorite-album-button',
    ALBUM_ACTIONS_BUTTON: 'album-actions-button',
    // ── /genres/:id (§3) ──────────────────────────────────────────────────────
    ALL_GENRES_LINK: 'all-genres-link',
    LOAD_MORE_BUTTON: 'load-more-button',
    // ── /search (§3; label-derived, supersedes placeholder-derived candidate) ─
    SEARCH_INPUT: 'search-input',
    CLEAR_SEARCH_BUTTON: 'clear-search-button',
    // ── /favorites (§3) ───────────────────────────────────────────────────────
    PLAY_ALL_BUTTON: 'play-all-button',
    QUEUE_BUTTON: 'queue-button',
    // ── /playlists (§3) ───────────────────────────────────────────────────────
    NEW_PLAYLIST_BUTTON: 'new-playlist-button',
    LOAD_MORE_PLAYLISTS_BUTTON: 'load-more-playlists-button',
    // ── /playlists/:id (§3) ───────────────────────────────────────────────────
    ALL_PLAYLISTS_LINK: 'all-playlists-link',
    DOWNLOAD_BUTTON: 'download-button',
    RENAME_BUTTON: 'rename-button',
    DELETE_BUTTON: 'delete-button',
    // ── /downloads (§3 — bounded seeded set, kept verbatim; see also
    //    deleteDownloadButton() for the general per-item shape) ────────────────
    DELETE_IN_RAINBOWS_BUTTON: 'delete-in-rainbows-button',
    DELETE_ROAD_TRIP_BUTTON: 'delete-road-trip-button',
    DELETE_KIND_OF_BLUE_BUTTON: 'delete-kind-of-blue-button',
    DELETE_DISCOVERY_BUTTON: 'delete-discovery-button',
    DELETE_NUDE_BUTTON: 'delete-nude-button',
    RETRY_BUTTON: 'retry-button',
    DELETE_VOODOO_BREW_BUTTON: 'delete-voodoo-brew-button',
    DELETE_ALL_DOWNLOADS_BUTTON: 'delete-all-downloads-button',
    // ── /stats (§3; the 25 ranked Top-Songs rows are factory-tier — see
    //    topSongRowButton() and the header note) ──────────────────────────────
    ALL_TIME_BUTTON: 'all-time-button',
    LAST_30_DAYS_BUTTON: 'last-30-days-button',
    // ── /queue (§3) ───────────────────────────────────────────────────────────
    QUEUE_TAB: 'queue-tab',
    HISTORY_LINK: 'history-link',
    SAVE_QUEUE_AS_PLAYLIST_BUTTON: 'save-queue-as-playlist-button',
    CLEAR_QUEUE_BUTTON: 'clear-queue-button',
    REMOVE_FROM_QUEUE_BUTTON: 'remove-from-queue-button',
    REMOVE_FROM_QUEUE_BUTTON_2: 'remove-from-queue-button-2',
    REMOVE_FROM_QUEUE_BUTTON_3: 'remove-from-queue-button-3',
    REMOVE_FROM_QUEUE_BUTTON_4: 'remove-from-queue-button-4',
    REMOVE_FROM_QUEUE_BUTTON_5: 'remove-from-queue-button-5',
    REMOVE_FROM_QUEUE_BUTTON_6: 'remove-from-queue-button-6',
    MORE_QUEUE_ACTIONS_BUTTON: 'more-queue-actions-button',
    MORE_QUEUE_ACTIONS_BUTTON_2: 'more-queue-actions-button-2',
    MORE_QUEUE_ACTIONS_BUTTON_3: 'more-queue-actions-button-3',
    MORE_QUEUE_ACTIONS_BUTTON_4: 'more-queue-actions-button-4',
    MORE_QUEUE_ACTIONS_BUTTON_5: 'more-queue-actions-button-5',
    MORE_QUEUE_ACTIONS_BUTTON_6: 'more-queue-actions-button-6',
    // History sub-tab (§3 /queue history block; row testids repeat per item)
    QUEUE_HISTORY_TAB: 'queue-history-tab',
    HISTORY_ROW: 'history-row',
    HISTORY_READD_BUTTON: 'history-readd-button',
    // §10.6 pagination affordance ("Show more (888 more)")
    QUEUE_SHOW_MORE_BUTTON: 'queue-show-more-button',
    // ── /player (§3) ──────────────────────────────────────────────────────────
    OPEN_FULL_PLAYER_LINK: 'open-full-player-link',
    PREVIOUS_TRACK_BUTTON: 'previous-track-button',
    PAUSE_BUTTON: 'pause-button',
    NEXT_TRACK_BUTTON: 'next-track-button',
    // ── /admin/users (§3 + §4 canonical reconciliation) ───────────────────────
    USERS_LINK: 'users-link',
    SYNC_STATUS_LINK: 'sync-status-link',
    EMAIL_TO_INVITE_INPUT: 'email-to-invite-input',
    INVITE_USER_BUTTON: 'invite-user-button',
    DEMOTE_BUTTON: 'demote-button',
    REMOVE_BUTTON: 'remove-button',
    REMOVE_BUTTON_2: 'remove-button-2',
    REMOVE_BUTTON_3: 'remove-button-3',
    DELETE_DATA_BUTTON: 'delete-data-button',
    DELETE_DATA_BUTTON_2: 'delete-data-button-2',
    DELETE_DATA_BUTTON_3: 'delete-data-button-3',
    DELETE_DATA_BUTTON_4: 'delete-data-button-4',
    PROMOTE_BUTTON: 'promote-button',
    PROMOTE_BUTTON_2: 'promote-button-2',
    PROMOTE_BUTTON_3: 'promote-button-3',
    REACTIVATE_BUTTON: 'reactivate-button',
    // ── /admin/sync (§3; per-label stat cards via syncStatCard()) ─────────────
    SYNC_NOW_BUTTON: 'sync-now-button',
    SYNC_STATE_CHIP: 'sync-state-chip',
    // ── /settings (§3 — names encode mock toggle state (ADMIN/OFF); kept
    //    verbatim, used by the mock-fidelity regression crawl) ────────────────
    SIGN_OUT_BUTTON: 'sign-out-button',
    VIEWER_ROLEADMIN_TAP_FOR_MEMBER_BUTTON: 'viewer-roleadmin-tap-for-member-button',
    OFFLINE_MODEOFF_BUTTON: 'offline-modeoff-button',
    FAIL_MUTATIONS_OPTIMISTIC_ROLLBACK_OFF_BUTTON: 'fail-mutations-optimistic-rollback-off-button',
    // ── /signin family (§3; retry-sign-in-button from §9 /signin/error) ───────
    G_SIGN_IN_WITH_GOOGLE_BUTTON: 'g-sign-in-with-google-button',
    DENIED_LINK: 'denied-link',
    ERROR_LINK: 'error-link',
    BACK_TO_SIGN_IN_LINK: 'back-to-sign-in-link',
    RETRY_SIGN_IN_BUTTON: 'retry-sign-in-button',
};
/**
 * Kebab-case slug of an accessible name / id — the contract's own derivation
 * rule (§ key notes: "kebab-case of the accessible name"). Diacritics folded,
 * non-alphanumerics collapse to single hyphens, no leading/trailing hyphen.
 */
export function toTestIdSlug(text) {
    const slug = text
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    return slug === '' ? 'unnamed' : slug;
}
/**
 * The contract's ordinal-duplicate convention for repeated same-label
 * controls: first instance is the bare id, subsequent ones get `-2`, `-3`, …
 * (e.g. `remove-from-queue-button-2`, `promote-button-3`).
 */
export function ordinalTestId(base, ordinal = 1) {
    const n = Math.max(1, Math.trunc(ordinal));
    return n === 1 ? base : `${base}-${n}`;
}
// ────────────────────────────────────────────────────────────────────────────
// Factories for §3's collapsed per-entity patterns. One factory per pattern;
// each produces valid kebab-case for any input.
// ────────────────────────────────────────────────────────────────────────────
/** §3 pattern `link:album-card →#/albums/*` (×27 home, ×24 genre, ×25 stats, ×8 favorites, ×3 artist). */
export const albumCardLink = (albumId) => `album-card-${toTestIdSlug(albumId)}-link`;
/** §3 pattern `link:artist-row →#/artists/*` (×40 browse, ×2 album detail). */
export const artistRowLink = (artistId) => `artist-row-${toTestIdSlug(artistId)}-link`;
/** §3 pattern `link:genre-row →#/genres/*` (×16 browse). */
export const genreRowLink = (genreId) => `genre-row-${toTestIdSlug(genreId)}-link`;
/** §3 pattern `link:playlist-row →#/playlists/*` (×40 playlists, ×1 home). */
export const playlistRowLink = (playlistId) => `playlist-row-${toTestIdSlug(playlistId)}-link`;
/** §3 pattern `btn:Actions for *` (aria-label "Actions for <title>"; ×20 home, ×42 playlist, ×21 favorites, …). */
export const actionsForButton = (title) => `actions-for-${toTestIdSlug(title)}-button`;
/** §3 pattern `button:Play *` (aria-label "Play <title>", e.g. "Play 15 Step" → `play-15-step-button`). */
export const playTrackButton = (title) => `play-${toTestIdSlug(title)}-button`;
/** §3 pattern `button:Favorite *` (aria-label "Favorite <title>"). */
export const favoriteTrackButton = (title) => `favorite-${toTestIdSlug(title)}-button`;
/** §3 pattern `button:Unfavorite *` (aria-label "Unfavorite <title>"). */
export const unfavoriteTrackButton = (title) => `unfavorite-${toTestIdSlug(title)}-button`;
/**
 * §3 pattern `btn:Move-reorder *` (×84 playlist, ×120 queue): the per-row
 * "Move up" / "Move down" controls (§9/§10.1 accessible names), repeated with
 * the contract's ordinal-duplicate convention.
 */
export const moveReorderButton = (direction, ordinal = 1) => ordinalTestId(`move-${direction}-button`, ordinal);
/** §3 /search populated state: `genre-filter-chip-<name>` (≤ 8 chips). */
export const genreFilterChip = (genreName) => `genre-filter-chip-${toTestIdSlug(genreName)}`;
/** §3 /admin/sync stat grid: `sync-stat-<label>` (Last run, Outcome, Tracks indexed, …). */
export const syncStatCard = (label) => `sync-stat-${toTestIdSlug(label)}`;
/**
 * §3 /stats Top-Songs row pattern `<rank>-<slug>-<plays>-plays-button`
 * (25 ranked rows, capped). The §6-enumerated instances are mock-seed
 * snapshots of this pattern (see header note).
 */
export const topSongRowButton = (rank, slug, plays) => `${Math.max(0, Math.trunc(rank))}-${toTestIdSlug(slug)}-${Math.max(0, Math.trunc(plays))}-plays-button`;
/** §3 /downloads per-item shape (aria-label "Delete <title>" → `delete-<slug>-button`). */
export const deleteDownloadButton = (title) => `delete-${toTestIdSlug(title)}-button`;
//# sourceMappingURL=testids.js.map